import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircle, CreditCard, ArrowLeft, ArrowRight, Users, FileText,
  HardDrive, Zap, Shield, Star, Sparkles, Loader2,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

export default function SubscriptionRenewalPage() {
  const navigate = useNavigate();
  const { subscription, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1=select plan, 2=confirm

  useEffect(() => {
    subscriptionsAPI.plans()
      .then(data => {
        setPlans(data);
        // Pre-select current plan if exists
        if (subscription?.plan_slug) {
          const current = data.find(p => p.slug === subscription.plan_slug);
          if (current) setSelectedPlan(current);
        }
        // If no current plan, select the first one
        if (!subscription?.plan_slug && data.length > 0) {
          setSelectedPlan(data[0]);
        }
      })
      .catch(err => toast.error('Failed to load plans'))
      .finally(() => setLoading(false));
  }, [subscription]);

  const handleRenew = async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    try {
      const result = await subscriptionsAPI.renew({
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
      });
      toast.success(result.message || 'Subscription updated successfully!');
      await refreshUser();
      navigate('/subscription');
    } catch (err) {
      toast.error(err.message || 'Failed to process renewal');
    } finally {
      setProcessing(false);
    }
  };

  const currentPrice = selectedPlan
    ? (billingCycle === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly)
    : 0;
  const monthlyEquivalent = selectedPlan
    ? (billingCycle === 'yearly' ? selectedPlan.price_yearly / 12 : selectedPlan.price_monthly)
    : 0;
  const savingsPercent = selectedPlan && billingCycle === 'yearly' && selectedPlan.price_monthly > 0
    ? Math.round((1 - selectedPlan.price_yearly / (selectedPlan.price_monthly * 12)) * 100)
    : 0;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loader2 size={32} className="spin" style={{ color: '#2563eb' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button
          onClick={() => navigate('/subscription')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', fontWeight: 500,
            padding: '4px 0', marginBottom: 12,
          }}
        >
          <ArrowLeft size={14} /> Back to Subscription
        </button>
        <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
          {subscription ? 'Change Your Plan' : 'Choose a Plan'}
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
          {subscription
            ? `You're currently on the ${subscription.plan_name || 'Free'} plan. Select a new plan below.`
            : 'Select a plan to get started with LedgerPro.'}
        </p>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28, padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 12, fontWeight: 700,
            background: step >= 1 ? '#2563EB' : '#e2e8f0',
            color: step >= 1 ? '#fff' : '#94a3b8',
            boxShadow: step >= 1 ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
          }}>1</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: step >= 1 ? '#2563EB' : '#94a3b8' }}>
            Select Plan
          </span>
        </div>
        <div style={{
          width: 48, height: 2, margin: '0 8px',
          background: step > 1 ? '#16a34a' : '#e2e8f0',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 12, fontWeight: 700,
            background: step >= 2 ? '#2563EB' : '#e2e8f0',
            color: step >= 2 ? '#fff' : '#94a3b8',
          }}>2</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: step >= 2 ? '#2563EB' : '#94a3b8' }}>
            Confirm & Pay
          </span>
        </div>
      </div>

      {/* Step 1: Plan Selection */}
      {step === 1 && (
        <>
          {/* Billing toggle */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3 }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                style={{
                  padding: '8px 24px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.2s',
                  background: billingCycle === 'monthly' ? '#fff' : 'transparent',
                  color: billingCycle === 'monthly' ? '#0f172a' : '#64748b',
                  boxShadow: billingCycle === 'monthly' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                style={{
                  padding: '8px 24px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.2s',
                  background: billingCycle === 'yearly' ? '#fff' : 'transparent',
                  color: billingCycle === 'yearly' ? '#0f172a' : '#64748b',
                  boxShadow: billingCycle === 'yearly' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                Yearly
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#16a34a',
                  background: '#dcfce7', padding: '2px 6px', borderRadius: 4,
                }}>
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          {/* Plan cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16, marginBottom: 28,
          }}>
            {plans.map(plan => {
              const isSelected = selectedPlan?.id === plan.id;
              const isCurrent = subscription?.plan_slug === plan.slug;
              const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
              const perMonth = billingCycle === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
              const features = (() => { try { return JSON.parse(plan.features || '[]'); } catch { return []; } })();

              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  style={{
                    border: `2px solid ${isSelected ? '#2563EB' : '#e2e8f0'}`,
                    borderRadius: 14, padding: 24, cursor: 'pointer',
                    transition: 'all 0.2s', position: 'relative', background: '#fff',
                    boxShadow: isSelected ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
                  }}
                >
                  {isCurrent && (
                    <div style={{
                      position: 'absolute', top: 12, right: 12, fontSize: 10, fontWeight: 700,
                      color: '#16a34a', background: '#dcfce7', padding: '3px 8px', borderRadius: 4,
                    }}>
                      Current
                    </div>
                  )}
                  {isSelected && !isCurrent && (
                    <div style={{
                      position: 'absolute', top: 12, right: 12,
                      width: 22, height: 22, borderRadius: '50%',
                      background: '#2563EB', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700,
                    }}>✓</div>
                  )}

                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                    {plan.name}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>
                    {fmt(perMonth)}
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>/mo</span>
                  </div>
                  {billingCycle === 'yearly' && plan.price_yearly > 0 && (
                    <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginBottom: 12 }}>
                      Billed {fmt(price)}/yr · Save {savingsPercent}%
                    </div>
                  )}
                  {billingCycle === 'monthly' && (
                    <div style={{ height: 16 }} />
                  )}

                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <Users size={12} /> {plan.max_users} users
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <FileText size={12} /> {plan.max_invoices_month === -1 ? 'Unlimited' : plan.max_invoices_month} invoices/mo
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <HardDrive size={12} /> {plan.max_storage_mb} MB storage
                    </div>
                  </div>

                  {features.length > 0 && (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {features.slice(0, 4).map((f, i) => (
                        <li key={i} style={{
                          fontSize: 12, color: '#334155', padding: '3px 0',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          <CheckCircle size={11} style={{ color: '#16a34a', flexShrink: 0 }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Continue button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setStep(2)}
              disabled={!selectedPlan}
              style={{
                padding: '12px 32px', border: 'none', borderRadius: 10,
                background: selectedPlan ? 'linear-gradient(135deg, #2563EB, #1D4ED8)' : '#e2e8f0',
                color: selectedPlan ? '#fff' : '#94a3b8',
                fontSize: 15, fontWeight: 600, cursor: selectedPlan ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: selectedPlan ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
              }}
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </>
      )}

      {/* Step 2: Confirm & Pay */}
      {step === 2 && selectedPlan && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>
          {/* Left: Order Summary */}
          <div style={{
            background: '#fff', borderRadius: 14, border: '1px solid #e8ecf1',
            overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Order Summary</h3>
            </div>

            <div style={{ padding: '20px 28px' }}>
              {/* Plan details */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{selectedPlan.name}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {billingCycle === 'yearly' ? 'Annual billing' : 'Monthly billing'}
                  </div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{fmt(currentPrice)}</div>
              </div>

              {/* Plan limits */}
              <div style={{
                background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                  What's included
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Users size={13} style={{ color: '#64748b' }} /> {selectedPlan.max_users} users
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FileText size={13} style={{ color: '#64748b' }} /> {selectedPlan.max_invoices_month === -1 ? 'Unlimited' : selectedPlan.max_invoices_month} invoices/mo
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <HardDrive size={13} style={{ color: '#64748b' }} /> {selectedPlan.max_storage_mb} MB storage
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Zap size={13} style={{ color: '#64748b' }} /> Priority support
                  </div>
                </div>
              </div>

              {/* Features */}
              {(() => {
                const features = (() => { try { return JSON.parse(selectedPlan.features || '[]'); } catch { return []; } })();
                return features.length > 0 ? (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                      Features
                    </div>
                    {features.map((f, i) => (
                      <div key={i} style={{
                        fontSize: 13, color: '#334155', padding: '4px 0',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <CheckCircle size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
                        {f}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {selectedPlan.description && (
                <p style={{ margin: '16px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                  {selectedPlan.description}
                </p>
              )}
            </div>
          </div>

          {/* Right: Payment Summary */}
          <div>
            <div style={{
              background: '#fff', borderRadius: 14, border: '1px solid #e8ecf1',
              overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
              position: 'sticky', top: 20,
            }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Payment Summary</h3>
              </div>

              <div style={{ padding: '16px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: '#475569' }}>
                  <span>{selectedPlan.name}</span>
                  <span>{fmt(currentPrice)}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: '#16a34a' }}>
                    <span>Annual discount</span>
                    <span>-{fmt(selectedPlan.price_monthly * 12 - selectedPlan.price_yearly)}</span>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #f1f5f9', margin: '12px 0', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Total due today</span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{fmt(currentPrice)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {billingCycle === 'yearly' ? 'Billed annually' : 'Billed monthly'}
                  </div>
                </div>
              </div>

              {/* Payment info placeholder */}
              <div style={{ padding: '0 24px 16px' }}>
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                  padding: '10px 14px', fontSize: 12, color: '#166534',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Shield size={14} />
                  <span>Your subscription will be activated immediately upon confirmation.</span>
                </div>
              </div>

              {/* CTA */}
              <div style={{ padding: '0 24px 24px' }}>
                <button
                  onClick={handleRenew}
                  disabled={processing}
                  style={{
                    width: '100%', padding: '14px 24px', border: 'none', borderRadius: 10,
                    background: processing ? '#94a3b8' : 'linear-gradient(135deg, #16a34a, #15803d)',
                    color: '#fff', fontSize: 15, fontWeight: 600,
                    cursor: processing ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: processing ? 'none' : '0 2px 8px rgba(22,163,74,0.3)',
                  }}
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} />
                      {subscription ? 'Activate Plan' : 'Start Subscription'}
                    </>
                  )}
                </button>
              </div>

              {/* Trust badges */}
              <div style={{
                padding: '12px 24px', borderTop: '1px solid #f1f5f9',
                display: 'flex', justifyContent: 'center', gap: 16,
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Shield size={11} /> Secure
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Star size={11} /> Cancel anytime
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Sparkles size={11} /> Instant activation
                </div>
              </div>
            </div>

            {/* Back button */}
            <button
              onClick={() => setStep(1)}
              style={{
                width: '100%', marginTop: 12, padding: '10px 24px',
                border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff',
                color: '#475569', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <ArrowLeft size={14} /> Back to Plans
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
